import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Copy, Check, Mail, Users, Download, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";

/* Mass Email — no sending provider involved. Pick who it's going to, pick a
   pre-written email, then copy the addresses (in Gmail-sized BCC batches) and
   the text, or open a Gmail compose window with it all filled in. */

const EDGE = "#E8DFCB";
const AUB = "#3E1F46";

const copy = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied.`);
  } catch {
    toast.error("Your browser blocked the clipboard — select the text and copy it manually.");
  }
};

const gmailLink = (bcc, subject, body) =>
  `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(bcc)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

export default function AdminMassEmail() {
  const [index, setIndex] = useState(null);
  const [audience, setAudience] = useState("priority");
  const [list, setList] = useState(null);
  const [templateKey, setTemplateKey] = useState("priority_invite");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [showAddresses, setShowAddresses] = useState(false);

  useEffect(() => {
    api.get("/admin/mail-merge")
      .then((r) => {
        setIndex(r.data);
        const t = r.data.templates.find((x) => x.key === "priority_invite") || r.data.templates[0];
        setSubject(t.subject);
        setBody(t.body);
      })
      .catch(() => toast.error("Couldn't load the mailing lists."))
      .finally(() => setLoading(false));
  }, []);

  const loadList = async (key) => {
    setListLoading(true);
    try {
      const r = await api.get("/admin/mail-merge/recipients", { params: { audience: key } });
      setList(r.data);
    } catch { toast.error("Couldn't load those addresses."); }
    finally { setListLoading(false); }
  };
  useEffect(() => { loadList(audience); }, [audience]);

  const pickTemplate = (key) => {
    const t = index.templates.find((x) => x.key === key);
    if (!t) return;
    setTemplateKey(key);
    setSubject(t.subject);
    setBody(t.body);
    if (t.audience && t.audience !== audience) setAudience(t.audience);
  };

  const csv = useMemo(() => {
    if (!list) return "";
    return "name,email\n" + list.people.map((p) => `"${(p.name || "").replace(/"/g, "'")}",${p.email}`).join("\n");
  }, [list]);

  const downloadCsv = () => {
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `heritage-${audience}-emails.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading mailing lists…</div>;
  if (!index) return null;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-mass-email">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ Send it yourself</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-1" style={{ color: AUB }}>Mass Email</h1>
          <p className="text-sm italic text-[#7A6A55] mt-1 max-w-2xl">
            Nothing is sent from here. Choose who it's for, pick a pre-written email, then copy the addresses
            and the words straight into Gmail — or open a Gmail draft with everything already filled in.
          </p>
        </div>
        <button type="button" onClick={() => loadList(audience)} data-testid="mass-email-refresh"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 border bg-white text-[#3C2A1E] text-[11px] font-bold uppercase tracking-[0.14em]" style={{ borderColor: EDGE }}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      <section className="bg-white border rounded p-4" style={{ borderColor: EDGE }} data-testid="mass-email-audiences">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] mb-3 inline-flex items-center gap-1.5">
          <Users className="w-4 h-4" /> Who is it going to?
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {index.audiences.map((a) => (
            <button key={a.key} type="button" onClick={() => setAudience(a.key)} data-testid={`mass-audience-${a.key}`}
              className="text-left p-3 border rounded transition-colors"
              style={audience === a.key
                ? { borderColor: AUB, backgroundColor: "#F2EAF3" }
                : { borderColor: EDGE, backgroundColor: "#fff" }}>
              <div className="font-serif text-[15px] text-[#3C2A1E]">{a.label}</div>
              <div className="text-[11px] text-[#7A6A55] italic">{a.blurb}</div>
              <div className="mt-1 text-[10px] tracking-[0.15em] uppercase font-bold" style={{ color: "#6E1E3A" }}>
                {a.count} {a.count === 1 ? "address" : "addresses"}
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-white border rounded p-4 space-y-3" style={{ borderColor: EDGE }} data-testid="mass-email-template">
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] inline-flex items-center gap-1.5">
          <Mail className="w-4 h-4" /> The email
        </div>
        <div className="flex flex-wrap gap-1.5">
          {index.templates.map((t) => (
            <button key={t.key} type="button" onClick={() => pickTemplate(t.key)} data-testid={`mass-template-${t.key}`}
              className="px-3 h-8 border rounded text-[10px] font-bold uppercase tracking-[0.12em]"
              style={templateKey === t.key
                ? { backgroundColor: AUB, borderColor: AUB, color: "#fff" }
                : { borderColor: EDGE, color: "#3C2A1E" }}>
              {t.label}
            </button>
          ))}
        </div>
        <label className="block text-xs">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="mass-subject"
            className="w-full h-10 px-3 border bg-white text-sm" style={{ borderColor: EDGE }} />
        </label>
        <label className="block text-xs">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Message — edit it however you like</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14} data-testid="mass-body"
            className="w-full p-3 border bg-white text-sm font-serif leading-relaxed" style={{ borderColor: EDGE }} />
        </label>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => copy(subject, "Subject")} data-testid="mass-copy-subject"
            className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E]" style={{ borderColor: EDGE }}>
            <Copy className="w-3.5 h-3.5" /> Copy subject
          </button>
          <button type="button" onClick={() => copy(body, "Message")} data-testid="mass-copy-body"
            className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#3E1F46] text-white text-[10px] font-bold uppercase tracking-[0.14em]">
            <Copy className="w-3.5 h-3.5" /> Copy message
          </button>
          <button type="button" onClick={() => copy(`Subject: ${subject}\n\n${body}`, "Subject + message")} data-testid="mass-copy-both"
            className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E]" style={{ borderColor: EDGE }}>
            <Copy className="w-3.5 h-3.5" /> Copy both
          </button>
        </div>
      </section>

      <section className="bg-white border rounded p-4 space-y-3" style={{ borderColor: EDGE }} data-testid="mass-email-addresses">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">
            Addresses — {list?.label || ""} ({list?.count ?? 0})
          </div>
          {listLoading && <Loader2 className="w-4 h-4 animate-spin text-[#9C8B73]" />}
        </div>

        {!list || list.count === 0 ? (
          <p className="text-xs italic text-[#9C8B73]" data-testid="mass-email-empty">
            No email addresses in that list yet.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => copy(list.comma_separated, `${list.count} addresses`)} data-testid="mass-copy-all"
                className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#6E1E3A] text-white text-[10px] font-bold uppercase tracking-[0.14em]">
                <Copy className="w-3.5 h-3.5" /> Copy all {list.count} addresses
              </button>
              <a href={gmailLink(list.batches[0].comma_separated, subject, body)} target="_blank" rel="noopener noreferrer"
                data-testid="mass-gmail-first"
                className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E]" style={{ borderColor: EDGE }}>
                <ExternalLink className="w-3.5 h-3.5" /> Open Gmail draft{list.batches.length > 1 ? " (batch 1)" : ""}
              </a>
              <button type="button" onClick={downloadCsv} data-testid="mass-download-csv"
                className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E]" style={{ borderColor: EDGE }}>
                <Download className="w-3.5 h-3.5" /> Download CSV
              </button>
              <button type="button" onClick={() => setShowAddresses((v) => !v)} data-testid="mass-toggle-addresses"
                className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E]" style={{ borderColor: EDGE }}>
                {showAddresses ? "Hide" : "Show"} the list
              </button>
            </div>

            <p className="text-[11px] italic text-[#7A6A55]">
              Paste the addresses into Gmail's <strong>BCC</strong> field so nobody sees anyone else's email.
              {list.batches.length > 1 && ` Gmail is happiest in smaller batches, so this list is split into ${list.batches.length} sends of up to ${list.batch_size}.`}
            </p>

            {list.batches.length > 1 && (
              <div className="space-y-1.5" data-testid="mass-batches">
                {list.batches.map((b) => (
                  <div key={b.index} className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="w-24 font-semibold text-[#3C2A1E]">Batch {b.index}</span>
                    <span className="text-[#7A6A55]">{b.count} addresses</span>
                    <button type="button" onClick={() => copy(b.comma_separated, `Batch ${b.index}`)} data-testid={`mass-copy-batch-${b.index}`}
                      className="inline-flex items-center gap-1 px-3 h-8 border rounded text-[10px] font-bold uppercase tracking-[0.12em] text-[#3C2A1E]" style={{ borderColor: EDGE }}>
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                    <a href={gmailLink(b.comma_separated, subject, body)} target="_blank" rel="noopener noreferrer"
                      data-testid={`mass-gmail-batch-${b.index}`}
                      className="inline-flex items-center gap-1 px-3 h-8 border rounded text-[10px] font-bold uppercase tracking-[0.12em] text-[#6E1E3A]" style={{ borderColor: EDGE }}>
                      <ExternalLink className="w-3 h-3" /> Gmail
                    </a>
                  </div>
                ))}
              </div>
            )}

            {showAddresses && (
              <div className="border rounded max-h-72 overflow-y-auto" style={{ borderColor: EDGE }} data-testid="mass-address-list">
                <table className="w-full text-sm">
                  <tbody className="divide-y" style={{ borderColor: "#F0E8D8" }}>
                    {list.people.map((p) => (
                      <tr key={p.email}>
                        <td className="px-3 py-1.5 font-serif text-[#3C2A1E]">{p.name || "—"}</td>
                        <td className="px-3 py-1.5 text-[#7A6A55]">{p.email}</td>
                        <td className="px-3 py-1.5 text-right">
                          <button type="button" onClick={() => copy(p.email, p.email)} className="text-[#6E1E3A]" aria-label={`Copy ${p.email}`}>
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <div className="bg-[#FBF6E9] border-l-4 border-[#C49247] p-4 text-xs text-[#7A5107]" data-testid="mass-email-note">
        <Check className="w-3.5 h-3.5 inline mr-1 mb-0.5" />
        Gmail sends up to about 500 emails a day on a personal account. If a list is bigger than that, send a batch or two
        today and the rest tomorrow — the batches above keep your place.
      </div>
    </div>
  );
}
